import { Button, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { CrudSection } from '../../components/CrudSection';
import { useEntityIndex } from '../../hooks/useEntityIndex';
import { formatQty } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { suggestPlural } from '../../lib/units';
import { UNIT_CONVERSIONS_PATH } from './stock';

/**
 * What one bottle is in millilitres, and what one case is in bottles.
 *
 * The engine that turns a 45 mL pour into a fraction of a bottle has read this table since
 * depletion shipped, and the two failures it hands back to the owner name it in so many words:
 * "add what one bottle is in mL", "the amounts do not match the shelf". Nothing on any screen could
 * add a row. The table shipped empty and stayed empty, so the recipe form offered exactly one unit
 * for every ingredient — the one the shelf is counted in — and the only way to say "45 mL of a rum
 * bought by the bottle" was to work out 0.06 of a bottle by hand and type that instead.
 *
 * A number somebody divided in their head is a number that is right once. It is wrong the first
 * time the bar buys the rum by the litre instead, and there is nothing on the screen afterwards
 * that says 0.06 was ever meant to be 45 mL.
 *
 * ## Said as a sentence, not as three columns
 *
 * The row is `from → to × factor`, which is the shape the walk needs and not a thing anybody says
 * out loud. The form asks it the way it is spoken — "one BOTTLE is 750 MILLILITRES" — because the
 * direction is the whole content of the fact and a form that asks for "from unit" and "to unit" is
 * a form where half the entries end up backwards. Backwards here is not a wrong label: it is a rum
 * costed at seven hundred and fifty times itself.
 *
 * ## Just this item, or everything
 *
 * A litre is a thousand millilitres wherever it appears; a bottle is 750 mL of this rum and 355 mL
 * of that beer. So the item is optional and, left empty, the row applies to everything — and an
 * item's own row wins over the global one for the same pair, which is `units.ts`'s rule and the
 * reason "bottle" can mean two things without either of them being wrong.
 */

interface ConversionRow {
  id?: unknown;
  inventoryItemId?: unknown;
  fromUnit?: unknown;
  toUnit?: unknown;
  factor?: unknown;
}

function RemoveButton({ row }: { row: ConversionRow }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => rawApi('DELETE', `${UNIT_CONVERSIONS_PATH}/${String(row.id)}`),
    onSuccess: () => {
      notifyDone(
        'Taken off',
        'Recipes written in that unit will need one that reaches the shelf before they can be costed.',
      );
      void queryClient.invalidateQueries({ queryKey: listKey(UNIT_CONVERSIONS_PATH) });
    },
    onError: (err: unknown) => notifyError(err, 'remove that conversion'),
  });

  return (
    <Button
      size="xs"
      variant="subtle"
      color="red"
      loading={remove.isPending}
      onClick={() =>
        modals.openConfirmModal({
          title: 'Take this conversion off?',
          children: (
            <Text size="sm">
              Any recipe written in {String(row.fromUnit ?? '')} for something counted in{' '}
              {String(row.toUnit ?? '')} stops being costable until another route between them is on file.
            </Text>
          ),
          labels: { confirm: 'Take it off', cancel: 'Keep it' },
          confirmProps: { color: 'red' },
          onConfirm: () => remove.mutate(),
        })
      }
    >
      Remove
    </Button>
  );
}

export function UnitConversions() {
  const items = useEntityIndex('inventoryItem');

  return (
    <CrudSection
      title="What one unit is in another"
      entityNoun="conversion"
      description="Recipes are written in the amounts a bartender pours and stock is counted in the containers you buy. Say what one is in the other here, once, and a recipe can ask for 45 mL of a rum you count in bottles."
      listPath={UNIT_CONVERSIONS_PATH}
      createPath={UNIT_CONVERSIONS_PATH}
      updatePathTemplate={`${UNIT_CONVERSIONS_PATH}/{id}`}
      emptyMessage="Nothing on file yet. Until there is, a recipe can only ask for an ingredient in the unit it is counted in."
      rowLabel={(row) =>
        `one ${String(row.fromUnit ?? '')} in ${String(row.toUnit ?? '')}${
          row.inventoryItemId ? ` for ${items.label(row.inventoryItemId)}` : ''
        }`
      }
      columns={[
        {
          accessor: 'fromUnit',
          title: 'This says',
          // The sentence, not the three columns it is stored as. "1 bottle = 750 mL" is checkable
          // at a glance; "bottle · mL · 750" is three facts the reader has to reassemble, and the
          // reassembly is where a backwards row goes unnoticed.
          //
          // Its right-hand side is a quantity like any other, and read straight off the row it said
          // "One case is 24 bottle". The left-hand unit only ever follows the word "one", which is
          // why one side of this sentence is asked about below and the other is not.
          render: (r) => (
            <Text size="sm">
              One {String(r.fromUnit ?? '')} is{' '}
              {formatQty(typeof r.factor === 'number' ? r.factor : undefined, String(r.toUnit ?? ''))}
            </Text>
          ),
        },
        {
          accessor: 'inventoryItemId',
          title: 'Applies to',
          render: (r) =>
            r.inventoryItemId ? (
              items.label(r.inventoryItemId)
            ) : (
              <Text size="sm" c="dimmed">
                Everything
              </Text>
            ),
        },
      ]}
      fields={[
        {
          name: 'inventoryItemId',
          label: 'Just for one item',
          type: 'entity',
          resource: 'inventoryItem',
          nullable: true,
          description:
            'Leave empty when it is always true — a litre is a thousand millilitres. Name the item when it is only true of that one: a bottle of rum is 750 ml and a bottle of beer is 355.',
        },
        {
          name: 'fromUnit',
          label: 'One of these',
          type: 'text',
          required: true,
          placeholder: 'bottle',
          // Frozen after create for the same reason the pair is the unique key: changing either
          // side turns "put the number right" into "quietly say something else about something
          // else", against recipes that were written when it meant the first thing.
          fixedAfterCreate: true,
          description: 'The bigger container, usually — the one you buy and count in',
        },
        {
          name: 'toUnit',
          label: 'Is this many of these',
          type: 'text',
          required: true,
          placeholder: 'ml',
          fixedAfterCreate: true,
          description: 'The amount a recipe is written in — ml, g, oz, shots',
        },
        {
          name: 'factor',
          label: 'How many',
          type: 'number',
          required: true,
          placeholder: '750',
          description: 'One bottle is 750 ml, so this is 750. One case is 24 bottles, so this is 24.',
          validate: (value) =>
            typeof value === 'number' && value > 0 ? null : 'Say how many, as a number above zero',
        },
        /*
         * The other place a unit word is first typed.
         *
         * The stock item form asks this for everything the bar COUNTS in. A unit that only ever
         * appears here — "one bottle is 45 shot", where no item is counted in shots — would
         * otherwise have no form anywhere able to say what more than one of it is called, and this
         * row's own sentence would read "One bottle is 45 shot" for good.
         *
         * Not frozen with the unit beside it: the pair is what the row IS and changing it says
         * something else about something else, while what the word's plural is remains an ordinary
         * fact anybody may have got wrong once. It fills itself in, and it never blocks the save.
         */
        {
          name: 'toUnitPlural',
          label: 'More than one of those',
          type: 'text',
          nullable: true,
          placeholder: 'ml',
          suggest: (values) => suggestPlural(values.toUnit as string | undefined),
          description:
            'What you would call several of them, filled in for you and worth a glance. Several ml are still ml; several bottle are bottles.',
        },
      ]}
      rowActions={(row) => <RemoveButton row={row} />}
    />
  );
}
